// @ts-nocheck
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRightIcon,
  ChartBarSquareIcon,
  ClipboardDocumentListIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  PlusIcon,
  TicketIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import Context from "../../context/Context";
import { fetchForms, fetchMetrics } from "../../context/forms/FormsAction";
import { URL } from "../../service/Service";
import Header from "../../components/TitleAndSubtitle";
import {
  ActivityBarChart,
  StatusDoughnutChart,
} from "../../components/dashboard/DashboardCharts";
import {
  formatExpirationDate,
  isFormExpired,
} from "../../utils/formExpiration";

function formatDate(timestamp) {
  if (!timestamp) return "Sem data";
  const [datePart, timePart = ""] = String(timestamp).split(" ");
  const normalizedDate = datePart?.replace(/:/g, "/");
  return `${normalizedDate || datePart}${timePart ? ` · ${timePart.slice(0, 5)}` : ""}`;
}
const money = (value) =>
  new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(
    Number(value || 0),
  );

const metricCards = [
  {
    key: "activeFormNumber",
    label: "Formulários ativos",
    description: "Formulários disponíveis para receber respostas",
    icon: CheckCircleIcon,
    tone: "bg-teal-50 text-teal-700",
  },
  {
    key: "formNumber",
    label: "Formulários",
    description: "Formulários existentes na plataforma",
    icon: ClipboardDocumentListIcon,
    href: "/admin/forms",
    linkLabel: "Gerir formulários",
    tone: "bg-emerald-50 text-emerald-700",
  },
  {
    key: "blockedFormNumber",
    label: "Formulários bloqueados",
    description: "Bloqueados administrativamente e indisponíveis ao público",
    icon: LockClosedIcon,
    href: "/admin/forms",
    linkLabel: "Rever bloqueios",
    tone: "bg-red-50 text-red-700",
  },
  {
    key: "userNumber",
    label: "Utilizadores",
    description: "Contas com acesso à plataforma",
    icon: UserGroupIcon,
    href: "/admin/users",
    linkLabel: "Gerir utilizadores",
    tone: "bg-sky-50 text-sky-700",
  },
  {
    key: "replyNumber",
    label: "Respostas",
    description: "Submissões recebidas em todos os formulários",
    icon: EnvelopeIcon,
    tone: "bg-violet-50 text-violet-700",
  },
];

const AdminDashboard = () => {
  const { state, dispatch } = useContext(Context);
  const metrics = state.metrics?.data || {};
  const forms = useMemo(
    () => Object.values(state.forms?.data || {}).reverse(),
    [state.forms?.data],
  );
  const [period, setPeriod] = useState(30);
  const [financial, setFinancial] = useState({ trend: [] });
  const [financialLoading, setFinancialLoading] = useState(true);
  const [supportTickets, setSupportTickets] = useState([]);
  const [supportLoading, setSupportLoading] = useState(true);

  useEffect(() => {
    fetchMetrics(`${URL}/api/admin/metrics`, {}, dispatch);
    fetchForms(`${URL}/api/admin/forms`, {}, dispatch);
  }, [dispatch]);

  useEffect(() => {
    let active = true;
    setFinancialLoading(true);
    fetch(`${URL}/api/dashboard/admin/financial-metrics?period=${period}`, {
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${sessionStorage.getItem("jwt") || ""}`,
      },
    })
      .then((response) => (response.ok ? response.json() : { trend: [] }))
      .then((data) => active && setFinancial(data))
      .catch(() => active && setFinancial({ trend: [] }))
      .finally(() => active && setFinancialLoading(false));
    return () => {
      active = false;
    };
  }, [period]);

  useEffect(() => {
    let active = true;
    setSupportLoading(true);
    fetch(`${URL}/api/support/tickets`, {
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${sessionStorage.getItem("jwt") || ""}`,
      },
    })
      .then((response) => (response.ok ? response.json() : []))
      .then((data) => active && setSupportTickets(Array.isArray(data) ? data : []))
      .catch(() => active && setSupportTickets([]))
      .finally(() => active && setSupportLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const recentForms = forms.slice(0, 4);
  const activeFormNumber = forms.filter(
    (form) => !form.deleted && form.status !== "PAUSED" && !isFormExpired(form),
  ).length;
  const pausedFormNumber = forms.filter(
    (form) => !form.deleted && form.status === "PAUSED" && !isFormExpired(form),
  ).length;
  const expiredFormNumber = forms.filter(
    (form) => !form.deleted && isFormExpired(form),
  ).length;
  const deletedFormNumber = forms.filter((form) => form.deleted).length;
  const dashboardMetrics = {
    ...metrics,
    activeFormNumber,
    blockedFormNumber: pausedFormNumber,
    replyNumber: financial.totalReplies,
  };
  const periodDescription =
    period === 0 ? "Em todo o período" : `Nos últimos ${period} dias`;
  const openSupportTickets = supportTickets.filter(
    (ticket) => !["RESOLVED", "CLOSED"].includes(ticket.status),
  );
  const waitingSupportTickets = supportTickets.filter(
    (ticket) => ticket.status === "WAITING_CUSTOMER",
  );
  const urgentSupportTickets = openSupportTickets.filter(
    (ticket) => ["HIGH", "URGENT"].includes(ticket.priority),
  );
  const recentSupportTickets = supportTickets.slice(0, 3);

  return (
    <main className="min-h-[calc(100vh-5rem)] bg-gradient-to-br from-slate-50 via-white to-emerald-50/50 px-4 py-7 sm:px-8 lg:px-12">
      <div className="mx-auto max-w-7xl">
        <Header
          eyebrow="Administração"
          title="Visão geral"
          description="Acompanhe a atividade da plataforma e aceda rapidamente às principais áreas de gestão."
          actions={
            <>
              <select
                value={period}
                onChange={(event) => setPeriod(Number(event.target.value))}
                className="min-h-11 rounded-xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 shadow-sm"
                aria-label="Período das métricas financeiras"
              >
                <option value={7}>7 dias</option>
                <option value={30}>30 dias</option>
                <option value={90}>90 dias</option>
                <option value={0}>Todo o período</option>
              </select>
              <Link
                to="/admin/users"
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 shadow-sm transition hover:border-emerald-200 hover:text-emerald-800"
              >
                <UserGroupIcon className="h-5 w-5" />
                Utilizadores
              </Link>
              <Link
                to="/admin/forms"
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-green-cl px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-green-eupago"
              >
                <PlusIcon className="h-5 w-5" />
                Novo formulário
              </Link>
            </>
          }
        />

        <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
          {metricCards.map((card) => {
            const Icon = card.icon;
            return (
              <article
                key={card.key}
                className="group rounded-3xl border border-slate-200/80 bg-white p-6 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-bold uppercase tracking-wider text-slate-500">
                      {card.label}
                    </p>
                    <p
                      className="mt-3 text-4xl font-extrabold tracking-tight text-slate-950"
                      aria-live="polite"
                    >
                      {card.key === "replyNumber" && financialLoading
                        ? "—"
                        : (dashboardMetrics[card.key] ?? "—")}
                    </p>
                  </div>
                  <span
                    className={`flex h-12 w-12 items-center justify-center rounded-2xl ${card.tone}`}
                  >
                    <Icon className="h-6 w-6" />
                  </span>
                </div>
                <p className="mt-3 min-h-10 text-sm leading-5 text-slate-500">
                  {card.key === "replyNumber"
                    ? financialLoading
                      ? "A atualizar…"
                      : periodDescription
                    : card.description}
                </p>
                {card.href && (
                  <Link
                    to={card.href}
                    className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-emerald-800"
                  >
                    {card.linkLabel}
                    <ArrowRightIcon className="h-4 w-4 transition group-hover:translate-x-1" />
                  </Link>
                )}
              </article>
            );
          })}
        </section>

        <section className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[
            [
              "Recebido",
              money(financial.paidAmount),
              `${financial.paidCount || 0} pagamentos · ${periodDescription.toLowerCase()}`,
            ],
            [
              "Pendente",
              money(financial.pendingAmount),
              `${financial.pendingCount || 0} referências · ${periodDescription.toLowerCase()}`,
            ],
            [
              "Reembolsado",
              money(financial.refundedAmount),
              `${money(financial.pendingRefundAmount)} solicitado · ${periodDescription.toLowerCase()}`,
            ],
            [
              "Pagamento médio",
              money(financial.averagePaidAmount),
              `Pagamentos concluídos · ${periodDescription.toLowerCase()}`,
            ],
          ].map(([label, value, detail]) => (
            <article
              key={label}
              className="rounded-2xl border border-slate-200/80 bg-white px-5 py-4 shadow-sm"
            >
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                {label}
              </p>
              <strong
                className="mt-2 block text-xl text-slate-950"
                aria-live="polite"
              >
                {financialLoading ? "—" : value}
              </strong>
              <span className="mt-1 block text-xs text-slate-500">
                {financialLoading ? "A atualizar…" : detail}
              </span>
            </article>
          ))}
        </section>

        <section className="mt-6 grid gap-6 xl:grid-cols-[1.35fr_0.85fr]">
          <article className="rounded-3xl border border-slate-200/80 bg-white p-6 shadow-sm">
            <h2 className="text-xl font-extrabold text-slate-950">
              Volume financeiro da plataforma
            </h2>
            <p className="mb-5 mt-1 text-sm text-slate-500">
              Montantes pagos por dia no período selecionado.
            </p>
            <ActivityBarChart
              labels={(financial.trend || []).map((point) =>
                point.date.slice(5),
              )}
              values={(financial.trend || []).map((point) => point.paidAmount)}
              label="Recebido (€)"
            />
          </article>
          <article className="rounded-3xl border border-slate-200/80 bg-white p-6 shadow-sm">
            <h2 className="text-xl font-extrabold text-slate-950">
              Estado da plataforma
            </h2>
            <p className="mb-5 mt-1 text-sm text-slate-500">
              Distribuição atual dos formulários.
            </p>
            <StatusDoughnutChart
              labels={["Ativos", "Bloqueados", "Expirados", "Removidos"]}
              values={[
                activeFormNumber,
                pausedFormNumber,
                expiredFormNumber,
                deletedFormNumber,
              ]}
              colors={["#10b981", "#f97316", "#dc2626", "#cbd5e1"]}
            />
          </article>
        </section>

        <section className="mt-6 overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-sm">
          <div className="flex flex-col gap-4 border-b border-slate-100 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700">
                <TicketIcon className="h-6 w-6" />
              </span>
              <div>
                <h2 className="text-xl font-extrabold text-slate-950">Controlo de suporte</h2>
                <p className="mt-1 text-sm text-slate-500">Pedidos dos clientes que precisam de acompanhamento.</p>
              </div>
            </div>
            <Link to="/support" className="inline-flex items-center gap-2 text-sm font-bold text-emerald-800 hover:text-emerald-600">
              Gerir todos os tickets
              <ArrowRightIcon className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid gap-px bg-slate-100 sm:grid-cols-3">
            {[
              ["Por tratar", openSupportTickets.length, ClockIcon, "text-amber-700 bg-amber-50"],
              ["A aguardar cliente", waitingSupportTickets.length, EnvelopeIcon, "text-sky-700 bg-sky-50"],
              ["Urgentes", urgentSupportTickets.length, ExclamationTriangleIcon, "text-red-700 bg-red-50"],
            ].map(([label, value, Icon, tone]) => (
              <div key={label} className="flex items-center gap-4 bg-white px-6 py-5">
                <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${tone}`}>
                  <Icon className="h-5 w-5" />
                </span>
                <div>
                  <strong className="block text-2xl text-slate-950">{supportLoading ? "—" : value}</strong>
                  <span className="text-sm text-slate-500">{label}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="divide-y divide-slate-100">
            {!supportLoading && recentSupportTickets.map((ticket) => (
              <Link key={ticket.id} to="/support" className="flex flex-col gap-2 px-6 py-4 transition hover:bg-slate-50 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p className="truncate font-bold text-slate-900">{ticket.subject}</p>
                  <p className="mt-1 truncate text-sm text-slate-500">{ticket.requesterEmail} · atualizado em {new Date(ticket.updatedAt).toLocaleString("pt-PT")}</p>
                </div>
                <span className={`w-fit rounded-full px-3 py-1 text-xs font-bold ${["HIGH", "URGENT"].includes(ticket.priority) ? "bg-red-50 text-red-700" : "bg-slate-100 text-slate-600"}`}>
                  {["HIGH", "URGENT"].includes(ticket.priority) ? "Prioritário" : "Ver ticket"}
                </span>
              </Link>
            ))}
            {!supportLoading && !recentSupportTickets.length && (
              <p className="px-6 py-8 text-center text-sm text-slate-500">Não existem tickets de suporte.</p>
            )}
            {supportLoading && (
              <p className="px-6 py-8 text-center text-sm text-slate-500">A atualizar o suporte…</p>
            )}
          </div>
        </section>

        <section className="mt-6 grid gap-6 lg:grid-cols-[1.55fr_0.75fr]">
          <div className="overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-sm">
            <div className="flex flex-col gap-3 border-b border-slate-100 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-xl font-extrabold text-slate-950">
                  Formulários recentes
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  Os últimos formulários adicionados à plataforma.
                </p>
              </div>
              <Link
                to="/admin/forms"
                className="text-sm font-bold text-emerald-800 hover:text-emerald-600"
              >
                Ver todos
              </Link>
            </div>

            {recentForms.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {recentForms.map((form) => (
                  <div
                    key={form.id}
                    className="flex flex-col gap-3 px-6 py-4 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0">
                      <p className="truncate font-bold text-slate-900">
                        {form.name || "Formulário sem nome"}
                      </p>
                      <p className="mt-1 truncate text-sm text-slate-500">
                        {form.userEmail || "Sem utilizador associado"} ·{" "}
                        {formatDate(form.timestamps?.[0])}
                      </p>
                      {form.expirationDate && (
                        <p
                          className={`mt-1 text-xs font-semibold ${isFormExpired(form) ? "text-red-700" : "text-slate-500"}`}
                        >
                          Disponível até{" "}
                          {formatExpirationDate(form.expirationDate)}
                        </p>
                      )}
                    </div>
                    <span
                      className={`w-fit rounded-full px-3 py-1 text-xs font-bold ${
                        isFormExpired(form)
                          ? "bg-red-50 text-red-700"
                          : form.status === "PAUSED"
                            ? "bg-red-50 text-red-700"
                            : "bg-emerald-50 text-emerald-700"
                      }`}
                    >
                      {isFormExpired(form)
                        ? "Expirado"
                        : form.status === "PAUSED"
                          ? "Bloqueado"
                          : "Ativo"}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-6 py-12 text-center text-sm text-slate-500">
                Ainda não existem formulários.
              </div>
            )}
          </div>

          <aside className="rounded-3xl bg-green-cl p-6 text-white shadow-sm">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10">
              <ChartBarSquareIcon className="h-6 w-6" />
            </span>
            <h2 className="mt-5 text-2xl font-extrabold !text-white">
              Centro de gestão
            </h2>
            <p className="mt-2 text-sm leading-6 text-emerald-50/80">
              Consulte formulários, altere responsáveis e acompanhe as contas da
              plataforma num só lugar.
            </p>
            <div className="mt-6 grid gap-3">
              <Link
                to="/admin/forms"
                className="inline-flex items-center justify-between rounded-xl bg-white px-4 py-3 text-sm font-bold text-green-cl transition hover:bg-emerald-50"
              >
                Gerir formulários
                <ArrowRightIcon className="h-4 w-4" />
              </Link>
              <Link
                to="/admin/users"
                className="inline-flex items-center justify-between rounded-xl border border-white/20 px-4 py-3 text-sm font-bold text-white transition hover:bg-white/10"
              >
                Gerir utilizadores
                <ArrowRightIcon className="h-4 w-4" />
              </Link>
            </div>
          </aside>
        </section>
      </div>
    </main>
  );
};

export default AdminDashboard;
